// FILE: src/components/session/SessionResultsView.js
//
// Reopenable review of ONE completed attempt — the destination of
// ?view=results&session=<uuid> (History "Review", the simulation-complete
// email, and a refresh of the screen shown right after a session ends).
//
// Read-only by construction: it renders the stored result through the same
// SessionReviewList the live results screen uses, and offers no action that
// could change an answer, the score, or the session.
//
// Security: the data comes from get_session_review (migration 097), which is
// owner-only. Signed-out, not-owned and nonexistent all land on the same
// "result not found" state, so nothing can be probed from the URL.
import React, { useCallback, useEffect, useState } from 'react';
import ScreenHeader from '../shared/ScreenHeader';
import SessionReviewList from './SessionReviewList';
import { buildHistoricalReviewModel } from './reviewModel';
import { loadSessionReview, isSessionId } from '../../services/sessionReviewService';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { getExamConfig } from '../../config/exams';
import { trackEvent } from '../../services/telemetryService';

const MODE_LABELS = { full: 'Full Exam', practice: 'Practice', targeted: 'Targeted', targetedTest: 'Targeted' };
const REASON_LABELS = { completed: 'Completed', early: 'Ended early', expired: 'Time expired' };

const styles = {
  page: { fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh', background: 'var(--bg)' },
  body: { maxWidth: '760px', margin: '0 auto', padding: '24px 16px 48px' },
  card: {
    background: '#fff', border: '1px solid var(--line)', borderRadius: '10px',
    padding: '18px 20px', marginBottom: '20px',
  },
  scoreRow: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' },
  score: (color) => ({ fontSize: '2.4rem', fontWeight: '800', color, lineHeight: 1 }),
  scoreMeta: { fontSize: '0.95rem', color: 'var(--muted)' },
  subline: { margin: '10px 0 0', fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.6 },
  statusPill: (color, bg) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '0.74rem',
    fontWeight: '700', color, background: bg, border: `1px solid ${color}`,
  }),
  notice: {
    background: 'var(--warn-soft)', border: '1px solid var(--warn)', borderRadius: '8px',
    padding: '12px 14px', marginBottom: '20px', fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--ink)',
  },
  centered: { textAlign: 'center', padding: '48px 16px', color: 'var(--muted)' },
  primaryBtn: {
    marginTop: '14px', padding: '10px 18px', borderRadius: '6px', border: 'none',
    background: 'var(--brand)', color: '#fff', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
  },
};

function fmtDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch (_) { return null; }
}

/** "104 min" / "1:44" style minute label for a duration in seconds. */
export function fmtMinutes(seconds) {
  if (typeof seconds !== 'number' || !isFinite(seconds) || seconds < 0) return null;
  const mins = Math.round(seconds / 60);
  return `${mins} min`;
}

/**
 * @param {object}   props
 * @param {string}   props.sessionId   the attempt to show (canonical identity)
 * @param {string}   [props.source]    how the learner arrived: history | email | completion | link
 * @param {function} [props.onNavigate] view switch, e.g. onNavigate('history')
 * @param {function} [props.onSignIn]  opens the sign-in modal for a signed-out visitor
 */
export default function SessionResultsView({ sessionId, source, onNavigate, onSignIn }) {
  const { user, loading: authLoading } = useLearnerAuth();
  const [state, setState] = useState({ status: 'loading', model: null });

  const valid = isSessionId(sessionId);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!valid) { setState({ status: 'not_found', model: null }); return undefined; }
    if (!user) { setState({ status: 'signed_out', model: null }); return undefined; }

    let cancelled = false;
    setState({ status: 'loading', model: null });
    (async () => {
      const res = await loadSessionReview(sessionId);
      if (cancelled) return;
      if (res.status === 'ok') {
        const model = buildHistoricalReviewModel(res.payload);
        if (!model) { setState({ status: 'not_found', model: null }); return; }
        setState({ status: 'ok', model });
        try {
          const ageDays = model.summary.endedAt
            ? Math.max(0, Math.round((Date.now() - new Date(model.summary.endedAt).getTime()) / 86400000))
            : null;
          trackEvent('historical_review_opened', 'usage', {
            exam_id: model.summary.examId,
            mode: model.summary.mode,
            from: source || 'link',
            question_count: model.items.length,
            status: model.summary.completionReason,
            session_age_days: ageDays != null ? ageDays : undefined,
          }, { examSessionId: model.summary.sessionId });
        } catch (_) { /* telemetry never blocks the page */ }
      } else if (res.status === 'not_found') {
        setState({ status: 'not_found', model: null });
      } else {
        setState({ status: 'error', model: null });
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, user, authLoading, valid, source]);

  const onFilterChange = useCallback((key) => {
    try {
      trackEvent('historical_review_filter_used', 'usage', { target: key, view: 'results' });
    } catch (_) { /* never blocks */ }
  }, []);

  const back = () => onNavigate?.('history');

  const shell = (children, eyebrow, title) => (
    <div style={styles.page} data-testid="session-results-view">
      <ScreenHeader
        variant="banner"
        coachPose="notepad"
        onBack={back}
        backLabel="Back to history"
        backTestId="session-results-back"
        eyebrow={eyebrow}
        title={title}
      />
      <div style={styles.body}>{children}</div>
    </div>
  );

  if (authLoading || state.status === 'loading') {
    return shell(
      <p style={styles.centered} data-testid="session-results-loading">Loading your results…</p>,
      'Your exam history', 'Simulation Results',
    );
  }

  if (state.status === 'signed_out') {
    return shell(
      <div style={styles.centered} data-testid="session-results-signin">
        <p>Sign in to view your results. These are your own answers, so we keep them private to your account.</p>
        <button type="button" style={styles.primaryBtn} onClick={() => onSignIn?.()} data-testid="session-results-signin-button">
          Sign in
        </button>
      </div>,
      'Your exam history', 'Simulation Results',
    );
  }

  if (state.status !== 'ok') {
    return shell(
      <div style={styles.centered} data-testid="session-results-not-found">
        <p>
          {state.status === 'error'
            ? 'We could not load this result just now. Please try again in a moment.'
            : 'We could not find that result on your account.'}
        </p>
        <button type="button" style={styles.primaryBtn} onClick={back} data-testid="session-results-history-button">
          See your session history
        </button>
      </div>,
      'Your exam history', 'Simulation Results',
    );
  }

  const { summary, items, notices } = state.model;
  const cfg = getExamConfig(summary.examId);
  const certLabel = summary.displayName || cfg?.displayName || (summary.examId || '').toUpperCase();
  const modeLabel = MODE_LABELS[summary.mode] || summary.modeName || 'Session';
  const reason = REASON_LABELS[summary.completionReason] || 'Completed';
  const pct = summary.percentage;
  const scoreColor = pct == null ? 'var(--ink)' : pct >= 80 ? 'var(--good)' : pct >= 60 ? 'var(--warn)' : 'var(--bad)';
  const timeLabel = fmtMinutes(summary.timeUsedSeconds);
  const allowance = summary.durationMinutes ? `${summary.durationMinutes} min` : null;

  const timeSentence = timeLabel
    ? (summary.timeUsedSource === 'question_timing'
        ? `about ${timeLabel} on questions${allowance ? ` (of ${allowance} allowed)` : ''}`
        : `${timeLabel} used${allowance ? ` of ${allowance} allowed` : ''}`)
    : (allowance ? `${allowance} allowed` : null);

  return shell(
    <>
      <div style={styles.card} data-testid="session-results-summary">
        <div style={styles.scoreRow}>
          <span style={styles.score(scoreColor)} data-testid="session-results-score">
            {pct == null ? '—' : `${pct}%`}
          </span>
          <span style={styles.scoreMeta} data-testid="session-results-meta">
            {summary.answered} of {summary.totalQuestions} answered
            {timeSentence ? ` · ${timeSentence}` : ''}
          </span>
          <span style={styles.statusPill(
            summary.completionReason === 'completed' ? 'var(--good)' : 'var(--warn)',
            summary.completionReason === 'completed' ? 'var(--good-soft)' : 'var(--warn-soft)',
          )} data-testid="session-results-status">
            {reason}
          </span>
        </div>
        <p style={styles.subline}>
          {certLabel} · {modeLabel}
          {fmtDateTime(summary.endedAt) ? ` · ${fmtDateTime(summary.endedAt)}` : ''}
          <br />
          This is your saved result for this attempt. It never changes, even if a question is
          later edited or re-keyed.
        </p>
      </div>

      {(notices.unverified > 0 || notices.reconstructedChanged > 0 || notices.keyChanged > 0 || notices.unavailable > 0) && (
        <div style={styles.notice} data-testid="session-results-notices">
          <strong>A note on this older attempt.</strong>{' '}
          {notices.reconstructedChanged > 0 && (
            <>{notices.reconstructedChanged} question{notices.reconstructedChanged === 1 ? ' has' : 's have'} been
              edited since you answered — those are shown as they appeared at the time. </>
          )}
          {notices.unverified > 0 && (
            <>{notices.unverified} question{notices.unverified === 1 ? '' : 's'} changed after your attempt in a way we
              can’t reconstruct, so the current version is shown. </>
          )}
          {notices.keyChanged > 0 && (
            <>{notices.keyChanged} answer key{notices.keyChanged === 1 ? ' has' : 's have'} changed since; you are
              seeing the key your answer was graded against. </>
          )}
          {notices.unavailable > 0 && (
            <>{notices.unavailable} question{notices.unavailable === 1 ? ' is' : 's are'} no longer in the bank and
              can’t be displayed. </>
          )}
          Your score and your answers are unchanged.
        </div>
      )}

      <SessionReviewList
        items={items}
        counts={{
          total: summary.total,
          correct: summary.correct,
          incorrect: summary.incorrect,
          skipped: summary.skipped,
          unanswered: summary.unanswered,
        }}
        onFilterChange={onFilterChange}
      />
    </>,
    `${certLabel} · ${modeLabel}`,
    'Simulation Results',
  );
}
